import { HttpError } from '@euler/lib/request/HttpError';
import {
  BiApplyOpsResult,
  BiDashboardOp,
  BiDashboardView,
} from '@euler/model/bi/dashboard';
import { useServiceFactory } from '@euler/services/factory';
import { useBehaviorSubject, useCreation } from '@euler/utils/hooks';
import { useCallback, useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { biErrorMessage, isBiRevisionConflict } from '../../functions/biErrors';

export type BiDashboardState =
  | { kind: 'loading' }
  | { kind: 'ready'; dashboard: BiDashboardView }
  /** Deleted, or not visible to the caller. */
  | { kind: 'missing' }
  | { kind: 'failed' };

/** The one message the page shows after a write. */
export type BiDashboardToast =
  /** A change landed as `revision`; it can be undone. */
  | { kind: 'applied'; text: string; revision: number }
  /** A tap in the chat changed it as `revision`; undone through the chat. */
  | { kind: 'chat_applied'; text: string; revision: number }
  /** An undo from the chat landed as `revision`; redone through the chat. */
  | { kind: 'chat_undone'; revision: number }
  /** An undo landed as `revision`; undoing it again redoes. */
  | { kind: 'undone'; revision: number }
  | { kind: 'notice'; text: string; tone: 'muted' | 'danger' };

/** What an apply came to; a refusal carries the sentence the toast shows. */
export type BiApplyOutcome =
  | { ok: true; result: BiApplyOpsResult }
  | { ok: false; message: string };

const kBusyText = '上一次修改还在保存，请稍候。';
const kConflictText = '看板刚被其他人修改过，已刷新到最新，请再操作一次。';

/**
 * One dashboard and every write to it. Each write carries the revision on
 * screen; a stale one reloads the dashboard and says so.
 */
export function useBiDashboard(dashboardId: string) {
  const { biService } = useServiceFactory();
  const state$ = useCreation(
    () => new BehaviorSubject<BiDashboardState>({ kind: 'loading' }),
    [],
  );
  const [state] = useBehaviorSubject(state$);
  // One write at a time: a second one would carry the revision the first
  // is about to replace, and read as someone else's change.
  const busy$ = useCreation(() => new BehaviorSubject(false), []);
  const [toast, setToast] = useState<BiDashboardToast | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let active = true;
    biService
      .dashboard(dashboardId)
      .then(dashboard => {
        if (active) state$.next({ kind: 'ready', dashboard });
      })
      .catch(error => {
        console.error('BI dashboard failed', dashboardId, error);
        if (!active) return;
        state$.next(
          error instanceof HttpError && error.code === 'resource_not_found'
            ? { kind: 'missing' }
            : { kind: 'failed' },
        );
      });
    return () => {
      active = false;
    };
  }, [biService, dashboardId, reload, state$]);

  const refresh = useCallback(() => setReload(value => value + 1), []);
  const replace = useCallback(
    (dashboard: BiDashboardView) => state$.next({ kind: 'ready', dashboard }),
    [state$],
  );
  const failed = useCallback(
    (error: unknown, what: string): string => {
      console.error(`BI dashboard ${what} failed`, dashboardId, error);
      if (isBiRevisionConflict(error)) {
        refresh();
        setToast({ kind: 'notice', text: kConflictText, tone: 'muted' });
        return kConflictText;
      }
      const text = biErrorMessage(error, '没有保存成功，请稍后重试。');
      setToast({ kind: 'notice', text, tone: 'danger' });
      return text;
    },
    [dashboardId, refresh],
  );

  /**
   * Applies ops on the revision on screen, or on `baseRevision` when the
   * change was prepared against an older copy (the card editor's).
   */
  const apply = useCallback(
    async (
      ops: BiDashboardOp[],
      /** What the undo toast calls the change, e.g. `已删除图表`. */
      text: string,
      baseRevision?: number,
    ): Promise<BiApplyOutcome> => {
      const current = state$.getValue();
      if (current.kind !== 'ready')
        return { ok: false, message: '看板还没有加载完成。' };
      if (busy$.getValue()) return { ok: false, message: kBusyText };
      const base = baseRevision ?? current.dashboard.revision;
      busy$.next(true);
      try {
        const result = await biService.applyDashboardOps(dashboardId, {
          baseRevision: base,
          ops,
        });
        replace(result.dashboard);
        // An apply that changed nothing writes no revision: nothing to undo.
        if (result.dashboard.revision !== base)
          setToast({
            kind: 'applied',
            text,
            revision: result.dashboard.revision,
          });
        return { ok: true, result };
      } catch (error) {
        return { ok: false, message: failed(error, 'apply') };
      } finally {
        busy$.next(false);
      }
    },
    [biService, busy$, dashboardId, failed, replace, state$],
  );

  /** Undoes the current revision, the only one that can be. */
  const undo = useCallback(async () => {
    const current = state$.getValue();
    if (current.kind !== 'ready' || busy$.getValue()) return;
    busy$.next(true);
    try {
      const dashboard = await biService.undoDashboard(dashboardId, {
        revision: current.dashboard.revision,
      });
      replace(dashboard);
      setToast({ kind: 'undone', revision: dashboard.revision });
    } catch (error) {
      failed(error, 'undo');
    } finally {
      busy$.next(false);
    }
  }, [biService, busy$, dashboardId, failed, replace, state$]);

  /**
   * Reads the dashboard again after a tap in its chat settled: applied
   * changes, an add, or an undo. A new revision gets its toast.
   */
  const settleChatTap = useCallback(
    async (outcome: 'changed' | 'undone') => {
      const current = state$.getValue();
      const before =
        current.kind === 'ready' ? current.dashboard.revision : null;
      try {
        const dashboard = await biService.dashboard(dashboardId);
        replace(dashboard);
        if (before == null || dashboard.revision === before) return;
        setToast(
          outcome === 'undone'
            ? { kind: 'chat_undone', revision: dashboard.revision }
            : {
                kind: 'chat_applied',
                text: '已按 AI 的建议修改看板',
                revision: dashboard.revision,
              },
        );
      } catch (error) {
        console.error('BI dashboard reload failed', dashboardId, error);
        refresh();
      }
    },
    [biService, dashboardId, refresh, replace, state$],
  );

  const dismissToast = useCallback(() => setToast(null), []);
  return {
    state,
    refresh,
    replace,
    apply,
    undo,
    settleChatTap,
    toast,
    dismissToast,
  };
}
